import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Banknote, ArrowDownCircle, ArrowUpCircle, Check, X, Plus } from "lucide-react";

const SOURCES = [
  { v: "fee_collection", l: "Cash from Fees" },
  { v: "donation", l: "Cash from Donations" },
  { v: "direct_upi_cheque", l: "Direct UPI / Cheque" },
  { v: "other", l: "Other" },
];

function AccountForm({ onDone }) {
  const [f, setF] = useState({ bank_name: "", account_number: "", ifsc_code: "", account_type: "Savings", branch_id: "", current_balance: 0 });
  const [divisions, setDivisions] = useState([]);
  useEffect(() => { api.get("/finance-settings/divisions").then(r => setDivisions(r.data)).catch(() => {}); }, []);
  const save = async () => {
    const div = divisions.find(d => d.division_id === f.branch_id);
    try { await api.post("/finance/bank-accounts", { ...f, current_balance: Number(f.current_balance), division_name: div?.name || "" }); toast.success("Account added"); setF({ bank_name: "", account_number: "", ifsc_code: "", account_type: "Savings", branch_id: "", current_balance: 0 }); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="nb-card p-4 bg-white space-y-2" data-testid="bank-account-form">
      <h3 className="display text-lg font-bold">Add Bank Account</h3>
      <div className="grid sm:grid-cols-2 gap-2">
        <input className="nb-input text-sm" placeholder="Bank Name" value={f.bank_name} onChange={e => setF({ ...f, bank_name: e.target.value })} data-testid="bank-name" />
        <input className="nb-input text-sm" placeholder="Account Number" value={f.account_number} onChange={e => setF({ ...f, account_number: e.target.value })} data-testid="bank-acc-no" />
        <input className="nb-input text-sm" placeholder="IFSC" value={f.ifsc_code} onChange={e => setF({ ...f, ifsc_code: e.target.value })} />
        <select className="nb-select text-sm" value={f.account_type} onChange={e => setF({ ...f, account_type: e.target.value })}><option>Savings</option><option>Current</option></select>
        <select className="nb-select text-sm" value={f.branch_id} onChange={e => setF({ ...f, branch_id: e.target.value })} data-testid="bank-division">
          <option value="">Map to Division / Branch…</option>
          {divisions.map(d => <option key={d.division_id} value={d.division_id}>{d.name}</option>)}
        </select>
        <input type="number" className="nb-input text-sm" placeholder="Opening Balance" value={f.current_balance} onChange={e => setF({ ...f, current_balance: e.target.value })} />
      </div>
      <button className="nb-btn" onClick={save} data-testid="bank-account-save"><Plus size={14} /> Add Account</button>
    </div>
  );
}

function Accounts({ role, refreshKey, onChange }) {
  const [accs, setAccs] = useState([]);
  const load = () => api.get("/finance/bank-accounts").then(r => setAccs(r.data)).catch(() => {});
  useEffect(() => { load(); }, [refreshKey]);
  return (
    <div className="space-y-4">
      {(role === "admin" || role === "founder") && <AccountForm onDone={() => { load(); onChange?.(); }} />}
      <div className="nb-card p-0 overflow-hidden" data-testid="bank-accounts-table">
        <div className="p-3 bg-[#93C5FD] font-bold text-sm">Institutional Accounts · Real-time Balances</div>
        <table className="nb-table text-sm">
          <thead><tr><th>Bank</th><th>Account</th><th>IFSC</th><th>Type</th><th>Division / Branch</th><th>Balance</th></tr></thead>
          <tbody>
            {accs.map(a => (
              <tr key={a.bank_account_id} data-testid={`bank-row-${a.bank_account_id}`}>
                <td className="font-bold">{a.bank_name}</td><td className="mono text-xs">{a.account_number}</td>
                <td className="mono text-xs">{a.ifsc_code}</td><td>{a.account_type}</td>
                <td className="text-xs" data-testid={`bank-division-${a.bank_account_id}`}>{a.division_name || "—"}</td>
                <td className="mono font-bold" data-testid={`bank-balance-${a.bank_account_id}`}>₹{(a.current_balance || 0).toLocaleString()}</td>
              </tr>
            ))}
            {accs.length === 0 && <tr><td colSpan={6} className="text-center py-6 text-slate-500">No accounts yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function BankActions({ refreshKey, onChange }) {
  const [accs, setAccs] = useState([]);
  const [dep, setDep] = useState({ bank_account_id: "", amount: "", source_category: "fee_collection", reference_no: "", date: "" });
  const [wd, setWd] = useState({ bank_account_id: "", amount: "", purpose: "", date: "" });
  const load = () => api.get("/finance/bank-accounts").then(r => setAccs(r.data)).catch(() => {});
  useEffect(() => { load(); }, [refreshKey]);
  const deposit = async () => {
    try { const r = await api.post("/finance/bank/deposit", { ...dep, amount: Number(dep.amount) }); toast.success(`Deposited. Balance ₹${r.data.current_balance.toLocaleString()}`); setDep({ ...dep, amount: "", reference_no: "" }); load(); onChange?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const withdraw = async () => {
    try { await api.post("/finance/bank/withdraw", { ...wd, amount: Number(wd.amount) }); toast.success("Withdrawal submitted for approval (pending)"); setWd({ ...wd, amount: "", purpose: "" }); onChange?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="grid lg:grid-cols-2 gap-4" data-testid="bank-actions">
      <div className="nb-card p-5 bg-[#86EFAC] space-y-2">
        <h3 className="display text-lg font-bold flex items-center gap-2"><ArrowDownCircle size={18} /> Cash Deposit</h3>
        <select className="nb-select text-sm" value={dep.bank_account_id} onChange={e => setDep({ ...dep, bank_account_id: e.target.value })} data-testid="deposit-account">
          <option value="">Select Bank Account…</option>
          {accs.map(a => <option key={a.bank_account_id} value={a.bank_account_id}>{a.bank_name} · {a.account_number}</option>)}
        </select>
        <input type="number" className="nb-input text-sm" placeholder="Amount" value={dep.amount} onChange={e => setDep({ ...dep, amount: e.target.value })} data-testid="deposit-amount" />
        <select className="nb-select text-sm" value={dep.source_category} onChange={e => setDep({ ...dep, source_category: e.target.value })} data-testid="deposit-source">
          {SOURCES.map(s => <option key={s.v} value={s.v}>{s.l}</option>)}
        </select>
        <input className="nb-input text-sm" placeholder="Reference / Slip No" value={dep.reference_no} onChange={e => setDep({ ...dep, reference_no: e.target.value })} />
        <input type="date" className="nb-input text-sm" value={dep.date} onChange={e => setDep({ ...dep, date: e.target.value })} />
        <button className="nb-btn w-full justify-center" onClick={deposit} data-testid="deposit-submit">Record Deposit (credits balance)</button>
      </div>
      <div className="nb-card p-5 bg-[#FDE047] space-y-2">
        <h3 className="display text-lg font-bold flex items-center gap-2"><ArrowUpCircle size={18} /> Cash Withdrawal</h3>
        <select className="nb-select text-sm" value={wd.bank_account_id} onChange={e => setWd({ ...wd, bank_account_id: e.target.value })} data-testid="withdraw-account">
          <option value="">Select Bank Account…</option>
          {accs.map(a => <option key={a.bank_account_id} value={a.bank_account_id}>{a.bank_name} · {a.account_number}</option>)}
        </select>
        <input type="number" className="nb-input text-sm" placeholder="Amount" value={wd.amount} onChange={e => setWd({ ...wd, amount: e.target.value })} data-testid="withdraw-amount" />
        <input className="nb-input text-sm" placeholder="Purpose / Reason" value={wd.purpose} onChange={e => setWd({ ...wd, purpose: e.target.value })} data-testid="withdraw-purpose" />
        <input type="date" className="nb-input text-sm" value={wd.date} onChange={e => setWd({ ...wd, date: e.target.value })} />
        <button className="nb-btn w-full justify-center" onClick={withdraw} data-testid="withdraw-submit">Submit Withdrawal (needs approval)</button>
      </div>
    </div>
  );
}

function Ledger({ role, refreshKey, onChange }) {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/finance/bank/movements").then(r => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, [refreshKey]);
  const act = async (mv, action) => {
    try { await api.post(`/finance/bank/movements/${mv}/${action}`, action === "reject" ? { reason: "Rejected" } : {}); toast.success(`${action}d`); load(); onChange?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const canApprove = role === "admin" || role === "founder";
  return (
    <div className="nb-card p-0 overflow-hidden" data-testid="bank-ledger">
      <div className="p-3 bg-[#C4B5FD] font-bold text-sm">Deposits & Withdrawals Ledger</div>
      <table className="nb-table text-sm">
        <thead><tr><th>Date</th><th>Type</th><th>Source/Purpose</th><th>Ref</th><th>Amount</th><th>Status</th>{canApprove && <th>Action</th>}</tr></thead>
        <tbody>
          {rows.map(m => (
            <tr key={m.movement_id} data-testid={`mv-row-${m.movement_id}`}>
              <td className="text-xs">{m.date}</td>
              <td>{m.movement_type === "deposit" ? <span className="nb-badge bg-[#86EFAC]">Deposit</span> : <span className="nb-badge bg-[#FDE047]">Withdrawal</span>}</td>
              <td className="text-xs">{m.movement_type === "deposit" ? (m.source_category || "").replace(/_/g, " ") : (m.purpose || "—")}</td>
              <td className="mono text-xs">{m.reference_no || "—"}</td>
              <td className="mono">₹{(m.amount || 0).toLocaleString()}</td>
              <td><span className={`nb-badge ${m.status === "approved" ? "bg-[#86EFAC]" : m.status === "rejected" ? "bg-[#FCA5A5]" : "bg-[#FDE047]"}`} data-testid={`mv-status-${m.movement_id}`}>{m.status}</span></td>
              {canApprove && <td>
                {m.status === "pending" ? (
                  <div className="flex gap-1">
                    <button className="nb-btn nb-btn-secondary px-2 py-1 text-xs" onClick={() => act(m.movement_id, "approve")} data-testid={`mv-approve-${m.movement_id}`}><Check size={12} /></button>
                    <button className="nb-btn nb-btn-danger px-2 py-1 text-xs" onClick={() => act(m.movement_id, "reject")} data-testid={`mv-reject-${m.movement_id}`}><X size={12} /></button>
                  </div>
                ) : <span className="text-xs opacity-50">—</span>}
              </td>}
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={canApprove ? 7 : 6} className="text-center py-6 text-slate-500">No movements yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

// role: "accountant" → Bank Actions ; "admin"/"founder" → Accounts + Ledger(+approvals)
export default function BankOps({ role }) {
  const [sub, setSub] = useState(role === "accountant" ? "actions" : "accounts");
  const [refreshKey, setRefreshKey] = useState(0);
  const refresh = () => setRefreshKey(k => k + 1);
  if (role === "accountant") {
    return (
      <div className="space-y-4" data-testid="bank-ops-accountant">
        <h2 className="display text-2xl font-bold flex items-center gap-2"><Banknote size={22} /> Bank Actions</h2>
        <BankActions refreshKey={refreshKey} onChange={refresh} />
        <Ledger role={role} refreshKey={refreshKey} onChange={refresh} />
      </div>
    );
  }
  const SUBS = [{ id: "accounts", l: "Accounts" }, { id: "ledger", l: "Deposits & Withdrawals" }];
  return (
    <div className="space-y-4" data-testid="bank-ops-admin">
      <h2 className="display text-2xl font-bold flex items-center gap-2"><Banknote size={22} /> Bank Accounts</h2>
      <div className="flex gap-2">
        {SUBS.map(s => <button key={s.id} onClick={() => setSub(s.id)} className={`nb-btn text-sm ${sub === s.id ? "" : "nb-btn-ghost"}`} data-testid={`bank-sub-${s.id}`}>{s.l}</button>)}
      </div>
      {sub === "accounts" ? <Accounts role={role} refreshKey={refreshKey} onChange={refresh} /> : <Ledger role={role} refreshKey={refreshKey} onChange={refresh} />}
    </div>
  );
}
